import { Country, useClickOutside } from 'react-easyrocket';
import { ChangeEvent, FC, useMemo, useRef, useState } from 'react';
import { Arrow } from 'src/components/svg/Arrows';
import classNames from 'classnames';
import { DropdownItem } from './components/DropdownItem';
import { useTranslation } from 'react-i18next';
import { SpriteFlagDisplay } from '../SpriteFlagDisplay';
import { IconName } from '../SpriteFlagDisplay/sprite/icon-types';
import { allCountriesData } from 'src/constants/index';

interface CountryDropdownProps {
  selectedCountry: Country | undefined;
  onSelect: (country: Country) => void;
  disabled?: boolean;
}

export const CountryDropdown: FC<CountryDropdownProps> = ({
  selectedCountry,
  onSelect,
  disabled
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const handleCloseDropdown = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  useClickOutside(dropdownRef, handleCloseDropdown);

  const handleSelectCountry = (country: Country) => {
    onSelect(country);
    setIsOpen(false);
    setSearchQuery('');
  };

  const filteredCountries = useMemo(() => {
    return allCountriesData.filter((country) =>
      country.name.toLowerCase().startsWith(searchQuery.toLowerCase())
    );
  }, [searchQuery]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleToggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prevState) => !prevState);
  };

  const validateAndLimitInputSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (/^[a-zA-Z]*$/.test(inputValue) && inputValue.length <= 4) {
      return;
    } else {
      event.target.value = searchQuery;
    }
  };

  return (
    <div ref={dropdownRef}>
      <div role="button" tabIndex={0} onClick={handleToggleDropdown} className="flex items-center">
        {isOpen && !disabled ? (
          <input
            inputMode="search"
            disabled={disabled}
            maxLength={4}
            onInput={validateAndLimitInputSearch}
            value={searchQuery}
            onChange={handleSearch}
            className="flex items-center h-full w-12 gap-2 p-2 justify-between hover:bg-gray-100 rounded-lg outline-none"
            autoFocus
          />
        ) : (
          <button
            disabled={disabled}
            className="flex items-center w-12 h-full gap-2 p-2 justify-between hover:bg-gray-100 rounded-lg">
            <SpriteFlagDisplay flag={selectedCountry?.alpha2 as IconName} />
          </button>
        )}
        <Arrow
          className={classNames(
            'w-5 h-5 fill-i_text transition-transform p-1',
            isOpen && 'rotate-180',
            disabled && 'cursor-no-drop'
          )}
        />
      </div>
      {isOpen && !disabled && (
        <ul className="absolute z-[40] top-auto left-auto -ml-5 mt-1 w-56 bg-white border border-i_border rounded-2xl shadow-lg max-h-48 overflow-y-auto">
          {filteredCountries.length ? (
            filteredCountries.map((country) => (
              <DropdownItem
                key={country.alpha2}
                country={country}
                onSelectCountry={handleSelectCountry}
              />
            ))
          ) : (
            <li className="p-2">{t('autocompleteEmptyState.noSuchCountry')}</li>
          )}
        </ul>
      )}
    </div>
  );
};
